import React, { useState, useEffect } from 'react';
import { useTranslations } from '../utils/translations';
import { cmsService } from '../services/cmsService';
import illustrationImage from '../assets/illustration.png';
import './ErrorDisplay.css';

const ErrorDisplay = ({ error, errorType = 'elearning/app/default', currentLanguage = 'en', onExit }) => {
  const { t } = useTranslations(currentLanguage);
  const [errorData, setErrorData] = useState({ title: 'Access Error' });
  const [isLoading, setIsLoading] = useState(true);
  
  // Get login URL from environment variable
  const loginUrl = process.env.REACT_APP_DRIVER_URL || 'https://yda-frontend-staging.eu-staging.kacdn.net';

  // Use passed error data or fetch from CMS
  useEffect(() => {
    const initializeErrorData = async () => {
      setIsLoading(true);
      try {
        if (error) {
          // Use error data passed from App.js
          if (typeof error === 'string') {
            // Legacy string format - convert to object
            setErrorData({ title: error });
          } else if (error.title) {
            // New object format with title and optional description
            setErrorData(error);
          } else {
            // Fallback
            setErrorData({ title: 'Access Error' });
          }
        } else {
          // Fetch from CMS if no error data passed
          const data = await cmsService.getErrorMessage(errorType, currentLanguage);
          setErrorData(data);
        }
      } catch (fetchError) {
        setErrorData({ title: 'Access Error' });
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeErrorData();
  }, [error, errorType, currentLanguage]);

  // Handle exit button click - same as audio bar (shows confirmation)
  const handleExit = () => {
    if (onExit) {
      onExit(); // This will show the confirmation dialog in App.js
    } else {
      // Default behavior
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    }
  };

  return (
    <div className="error-display" style={{ backgroundImage: `url(${illustrationImage})` }}>
      <div className="error-container">
        <div className="error-content">
          <h2 className="error-title">
            {isLoading ? t('errors.general.message') : errorData.title}
          </h2>
          {!isLoading && errorData.description && (
            <p className="error-description">{errorData.description}</p>
          )}
          {errorType === 'elearning/app/callback/authmissing' && (
            <div className="auth-error-extra">
              <p className="auth-message">
                {t('errors.auth.loginAgain')}{' '}
                <a href={loginUrl} className="login-link">
                  {t('errors.auth.clickHere')}
                </a>
              </p>
            </div>
          )}
        </div>
        <div className="error-actions">
          <button className="exit-button" onClick={handleExit}>
            {t('ui.navigation.exit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;
